import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { conMovimiento, contar, entrada, gsap, levantar, revelar, useGSAP } from '../animaciones';
import { ErrorDeApi, api, type Cuenta0, type MarcaResumen } from '../api';
import { Emblema } from '../componentes/emblema';
import { Igor } from '../componentes/igor';
import { Icono, type NombreDeIcono } from '../iconos';
import { supabase } from '../supabase';
import { SelectorDeIdioma, useIdioma } from '../idioma';
import { BotonDeTema } from '../tema';
import { olvidarPerfilPendiente, perfilPendiente } from './registro';

/**
 * La portada de la cuenta (referencias del usuario, 24-09-2026: OmNomad, Bionova, DocHands y Clickity): qué es
 * IGOR en un bloque grande de color, un mosaico de tarjetas pastel con lo de la cuenta (crear marca, tus marcas,
 * créditos, plan y el mes) y las marcas con su avance y sus métricas.
 */

const PASOS: Array<{ icono: NombreDeIcono; titulo: string; texto: string }> = [
  { icono: 'marca', titulo: 'Entendemos tu marca', texto: 'Tu negocio, tu cliente, tus redes y tu estilo.' },
  { icono: 'plan', titulo: 'Planeamos tu mes', texto: 'Qué publicar, cuándo y en qué formato.' },
  { icono: 'disenos', titulo: 'Creamos el contenido', texto: 'Textos, diseños y reels con presentadora.' },
  { icono: 'publicar', titulo: 'Publicamos y medimos', texto: 'Programado en tus redes y con su resultado.' },
];

const num = (n: number, locale: string) => n.toLocaleString(locale);
const nombreDe = (correo?: string) => {
  const n = (correo ?? '').split('@')[0]?.split(/[._-]+/)[0] ?? '';
  return n ? n[0]!.toUpperCase() + n.slice(1) : '';
};
const fecha = (iso: string | null | undefined, locale: string) => (iso ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long' }) : '');

export function Marcas() {
  const ir = useNavigate();
  const { t, locale, idioma } = useIdioma();
  const [c, setC] = useState<Cuenta0 | null>(null);
  const [error, setError] = useState('');
  const [sinPrueba, setSinPrueba] = useState<'repetida' | 'desechable' | 'limite' | null>(null);
  const [nombre, setNombre] = useState('');
  const [comoFunciona, setComoFunciona] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [codigoAmigo, setCodigoAmigo] = useState('');
  const [errorCodigo, setErrorCodigo] = useState('');
  const raiz = useRef<HTMLElement>(null);
  const campo = useRef<HTMLInputElement>(null);

  const cargar = () => api.cuenta().then(setC).catch((e: Error) => setError(e.message));
  useEffect(() => {
    // Preparar la cuenta va primero: a quien entra por primera vez le crea su espacio y sus créditos de
    // bienvenida. Si ya los tiene, no hace nada, así que se puede llamar cada vez que se entra.
    let ref: string | null = null;
    try { ref = localStorage.getItem('igor-ref'); } catch { /* sin almacenamiento */ }
    void api.arrancar(ref, perfilPendiente())
      .then((r) => {
        try { localStorage.removeItem('igor-ref'); } catch { /* nada */ }
        olvidarPerfilPendiente();
        if (r.prueba === 'repetida' || r.prueba === 'desechable' || r.prueba === 'limite') setSinPrueba(r.prueba);
      })
      .catch(() => undefined)
      .then(cargar);
  }, []);

  const { contextSafe } = useGSAP(
    () =>
      conMovimiento(() => {
        if (!c) return;
        revelar('.bienvenida h1', { por: 'lineas', cada: 0.12 });
        entrada('.bienvenida-sigla > span', { cada: 0.08 });
        entrada('.bienvenida-flotante, .bienvenida-chips > *', { retraso: 0.3, cada: 0.06 });
        entrada('.mosaico > *', { retraso: 0.25, cada: 0.07 });
        entrada('.marca-resumen', { retraso: 0.4, cada: 0.05 });
        gsap.from('.bienvenida-mascota', { y: 60, scale: 0.9, opacity: 0, duration: 0.9, ease: 'back.out(1.6)', delay: 0.2 });
        // Se mueve pivotando en la base (que queda fuera del bloque), así nunca se ve el borde recortado de la foto.
        gsap.fromTo('.bienvenida-mascota', { rotate: -2 }, { rotate: 2, scale: 1.02, transformOrigin: '50% 100%', duration: 2.6, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 1.1 });
        gsap.utils.toArray<Element>('.bienvenida-orbita').forEach((el, i) => gsap.to(el, { rotate: i % 2 ? -360 : 360, duration: 40 + i * 20, ease: 'none', repeat: -1 }));
        contar(raiz.current, 0.2);
      }),
    { scope: raiz, dependencies: [Boolean(c)] },
  );
  const sobre = {
    onMouseEnter: contextSafe((e: React.MouseEvent) => levantar(e.currentTarget, true)),
    onMouseLeave: contextSafe((e: React.MouseEvent) => levantar(e.currentTarget, false)),
  };

  const irACrear = () => {
    campo.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    campo.current?.focus({ preventScroll: true });
  };

  async function crear(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const { brand } = await api.crearMarca(nombre.trim());
      ir(`/marca/${brand.id}`);
    } catch (e) {
      // Por encima del plan: se ofrece la marca adicional con su costo en créditos (30-09-2026).
      const extra = e instanceof ErrorDeApi ? (e.datos?.marcaExtra as number | undefined) : undefined;
      if (extra && window.confirm(t('{mensaje}\n\n¿Sumar «{nombre}» por {n} créditos al mes? Se descuentan de tus créditos cada mes; si no alcanzan, la marca queda en pausa sin perder nada.', { mensaje: (e as Error).message, nombre: nombre.trim(), n: extra }))) {
        try {
          const { brand } = await api.crearMarca(nombre.trim(), true);
          return ir(`/marca/${brand.id}`);
        } catch (e2) {
          return setError((e2 as Error).message);
        }
      }
      setError((e as Error).message);
    }
  }

  // Borrar con una ventana propia (07-10-2026): con window.prompt, si el navegador bloqueaba la ventanita no pasaba
  // nada, y el error de un nombre mal escrito salía arriba de la página, donde no se veía. «La borré y no se borró».
  const [aBorrar, setABorrar] = useState<MarcaResumen | null>(null);
  const borrar = (m: MarcaResumen) => setABorrar(m);

  const marcas = c?.marcas ?? [];
  const cr = c?.creditos;
  const limite = c?.limite ?? null;
  const puedeCrear = limite === null || marcas.length < limite;
  const tot = marcas.reduce(
    (s, m) => ({ prog: s.prog + m.programadas, pub: s.pub + m.publicadas, int: s.int + m.interacciones }),
    { prog: 0, pub: 0, int: 0 },
  );
  const nombreMes = c?.mes ? new Date(`${c.mes}-01T12:00:00Z`).toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' }) : t('mes');
  const quien = nombreDe(c?.usuario.email);
  const nombrePlan = c?.plan?.name ?? (c?.workspace?.estado === 'trial' ? t('Prueba gratis') : t('Sin plan'));
  const enlace = c?.referidos ? `${location.origin}/?ref=${c.referidos.codigo}` : '';
  async function usarCodigo(e: React.FormEvent) {
    e.preventDefault();
    setErrorCodigo('');
    try {
      await api.usarCodigo(codigoAmigo.trim());
      setCodigoAmigo('');
      await cargar();
    } catch (e) {
      setErrorCodigo((e as Error).message);
    }
  }
    async function copiar() {
    try { await navigator.clipboard.writeText(enlace); } catch { window.prompt(t('Copia tu enlace:'), enlace); return; }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <main className="lienzo suelto cuenta-portada" ref={raiz}>
      <header className="cuenta-barra">
        <span className="logo-fila"><b className="logo-igor">IGOR</b><span className="logo-lema">{t('Ideas que Generan Oportunidades Reales')}</span></span>
        <span className="fila-acciones">
          <button className="btn claro chico" onClick={() => ir('/perfil')}>{t('Mi perfil')}</button>
          <SelectorDeIdioma />
            <BotonDeTema />
          <span className="cuenta-yo">
            <span className="yo">{quien.slice(0, 2).toUpperCase() || '··'}</span>
            <span className="col" style={{ gap: 0, minWidth: 0 }}>
              <b>{quien || t('Tu cuenta')}</b>
              <span className="apunte">{c?.usuario.email}</span>
            </span>
            <button className="btn-icono" title={t('Salir')} aria-label={t('Salir')} onClick={() => void supabase.auth.signOut({ scope: 'global' })}>
              <Icono nombre="cerrar" clase="ic-sm" />
            </button>
          </span>
        </span>
      </header>

      {error && <p className="error">{error}</p>}
      {c?.suscripcion?.status === 'canceled' && (
        <p className="aviso" role="status">
          {t('Tu plan terminó. Puedes ver y descargar todo lo tuyo; para seguir creando, elige un plan o recarga créditos.')}{' '}
          <a href="/perfil">{t('Elegir plan')}</a>
        </p>
      )}
      {c?.suscripcion?.status === 'past_due' && (
        <p className="aviso" role="alert">
          {t('No pudimos cobrar tu plan. Actualiza tu tarjeta en Tu perfil para seguir creando; mientras tanto, todo lo tuyo sigue guardado.')}{' '}
          <a href="/perfil">{t('Actualizar tarjeta')}</a>
        </p>
      )}
      {sinPrueba && (
        <p className="aviso" role="status">
          {sinPrueba === 'repetida'
            ? t('Ya usaste tu prueba gratis con este correo, así que esta cuenta empieza sin créditos de bienvenida. Elige un plan en Tu perfil para seguir.')
            : sinPrueba === 'limite'
              ? t('Hoy se crearon varias cuentas desde tu conexión, así que tu prueba gratis queda en espera. Se activa sola cuando vuelvas a entrar desde otra red (por ejemplo, los datos del celular) o mañana.')
              : t('Con correos temporales no damos créditos de bienvenida. Entra con tu correo de siempre para usar tu prueba gratis.')}
        </p>
      )}

      <section className="bienvenida">
        <div className="bienvenida-txt">
          <span className="bienvenida-hola">{quien ? t('Hola, {nombre}', { nombre: quien }) : t('Hola')} 👋</span>
          <p className="bienvenida-sigla" aria-label={`IGOR: ${t('Ideas que Generan Oportunidades Reales')}`}>
            {/* Las iniciales forman IGOR solo en español; en inglés el lema va sin resaltar. */}
            {t('Ideas que Generan Oportunidades Reales').split(' ').map((p) => (idioma !== 'es' || p[0] === p[0]!.toLowerCase() ? <span key={p}>{p}</span> : <span key={p}><b>{p[0]}</b>{p.slice(1)}</span>))}
          </p>
          <h1>{t('Tu equipo de contenido con IA, listo cada mes.')}</h1>
          <p>{t('IGOR entiende tu marca, planea tu mes, crea los textos, los diseños y los reels, los publica en tus redes y te cuenta cómo les fue.')}</p>
          <span className="fila-acciones">
            <button className="btn-lima" onClick={irACrear}>
              {t('Crear una marca')} <span className="flecha-circulo"><Icono nombre="flecha-larga" clase="ic-sm" /></span>
            </button>
            <button className="btn-vidrio" onClick={() => setComoFunciona(!comoFunciona)}>
              <Icono nombre="ayuda" clase="ic-sm" /> {comoFunciona ? t('Ocultar') : t('Cómo funciona')}
            </button>
          </span>
        </div>
        <div className="bienvenida-centro" aria-hidden="true">
          <span className="bienvenida-orbita o1" />
          <span className="bienvenida-orbita o2" />
          <span className="bienvenida-orbita o3" />
          <Igor pose="saluda" clase="bienvenida-mascota" />
        </div>
        <div className="bienvenida-lado">
          <div className="bienvenida-flotante">
            <span className="titulo-sec">
              <b className="num grande" data-cuenta={tot.prog}>{tot.prog}</b>
              <span className="flecha-mini"><Icono nombre="flecha-larga" clase="ic-sm" /></span>
            </span>
            <span className="apunte">{t('piezas programadas en {mes}', { mes: nombreMes })}</span>
            <span className="avatares">
              {marcas.slice(0, 4).map((m) => <Emblema key={m.id} id={m.id} nombre={m.name} logo={m.logoUrl} clase="mini" />)}
              {marcas.length > 4 && <span className="mas-avatar">+{marcas.length - 4}</span>}
            </span>
          </div>
          <div className="bienvenida-chips">
            {['Estrategia', 'Diseños', 'Reels con IA', 'Programación', 'Medición'].map((chip) => (
              <span key={chip}>{t(chip)} <Icono nombre="flecha-larga" clase="ic-sm" /></span>
            ))}
          </div>
        </div>
      </section>

      {comoFunciona && (
        <section className="como-funciona">
          {PASOS.map((p, i) => (
            <div key={p.titulo} className="paso-igor">
              <span className="paso-igor-num num">{String(i + 1).padStart(2, '0')}</span>
              <span className={`an-chip tono-${['violeta', 'cielo', 'coral', 'lima'][i]}`}><Icono nombre={p.icono} clase="ic-sm" /></span>
              <b>{t(p.titulo)}</b>
              <span className="apunte">{t(p.texto)}</span>
            </div>
          ))}
        </section>
      )}

      {c && (
        <>
          <section className="mosaico">
            <div className="mos mos-crear">
              <Titular icono="mas" texto={t('Crea una marca')} />
              {puedeCrear ? (
                <>
                  <span className="mos-texto">{t('Te hago las preguntas y armo su estrategia.')}</span>
                  <form className="mos-campo" onSubmit={crear}>
                    <Icono nombre="marca" clase="ic-sm" />
                    <input ref={campo} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder={t('¿Cómo se llama el negocio?')} aria-label={t('Nombre del negocio')} />
                    <button className="btn-negro" disabled={!nombre.trim()}>{t('Crear')}</button>
                  </form>
                </>
              ) : (
                <span className="mos-texto">{limite === 1 ? t('Tu plan permite 1 marca. Borra una que ya no uses o mejora tu plan para crear otra.') : t('Tu plan permite {n} marcas. Borra una que ya no uses o mejora tu plan para crear otra.', { n: limite ?? 0 })}</span>
              )}
            </div>

            <div className="mos mos-marcas">
              <Titular icono="marcas" texto={t('Tus marcas')} />
              <b className="mos-cifra num">{marcas.length}<small> {t('de {n}', { n: limite ?? '∞' })}</small></b>
              <div className="mos-arcos" aria-hidden="true"><i /><i /><i /><i /></div>
              <div className="mos-pila">
                <div className="mos-logos">
                  {marcas.map((m) => (
                    <button key={m.id} title={m.name} onClick={() => ir(`/marca/${m.id}`)}><Emblema id={m.id} nombre={m.name} logo={m.logoUrl} /></button>
                  ))}
                </div>
                {puedeCrear && <button className="mas-negro" aria-label={t('Nueva marca')} onClick={irACrear}><Icono nombre="mas" clase="ic-sm" /></button>}
              </div>
            </div>

            <div className="mos mos-creditos">
              <span className="titulo-sec">
                <Titular icono="creditos" texto={t('Tu plan y tus créditos')} />
                <span className="mos-etiqueta">{nombrePlan}</span>
              </span>
              <b className="mos-cifra grande num">{cr?.sinLimite ? '∞' : <span data-cuenta={cr?.disponibles ?? 0}>{cr?.disponibles ?? 0}</span>}</b>
              <span className="mos-texto">{t('créditos disponibles · usaste {usados} y recibiste {recibidos} en {mes}', { usados: num(cr?.usadosEsteMes ?? 0, locale), recibidos: num(cr?.recibidosEsteMes ?? 0, locale), mes: nombreMes })}</span>
              <div className="mos-tarjetita">
                <span>
                  <span className="dato-etiqueta">{c.plan ? t('Plan {nombre}', { nombre: c.plan.name }) : t('Tu plan')}</span>
                  <b>{c.plan ? t('{precio} USD al mes', { precio: num(Number(c.plan.price_usd_month), locale) }) : nombrePlan}</b>
                  <span className="dato-etiqueta">{c.plan ? `${t('{n} créditos', { n: num(c.plan.monthly_credits, locale) })}${c.plan.human_review ? ` · ${t('revisión humana')}` : ''}` : t('Elige un plan para seguir')}</span>
                </span>
                <span>
                  <span className="dato-etiqueta">{cr?.vencenPronto ? t('Vencen pronto') : c.suscripcion?.cancel_at ? t('Termina') : t('Se renueva')}</span>
                  <b>{cr?.vencenPronto ? t('{n} el {fecha}', { n: num(cr.vencenPronto.cantidad, locale), fecha: fecha(cr.vencenPronto.fecha, locale) }) : fecha(c.suscripcion?.cancel_at ?? c.suscripcion?.current_period_end, locale) || '—'}</b>
                  {cr?.vencenPronto && c.suscripcion?.current_period_end && <span className="dato-etiqueta">{t('Se renueva el {fecha}', { fecha: fecha(c.suscripcion.current_period_end, locale) })}</span>}
                </span>
              </div>
            </div>

            {c.referidos ? (
              <div className="mos mos-plan mos-referidos">
                <Titular icono="corazon" texto={t('Refiere a tus amigos')} />
                <b className="mos-nombre">{t('{n} + {n} créditos', { n: c.referidos.recompensa })}</b>
                <span className="mos-texto">{t('Por cada amigo que compre un plan de IGOR, tú recibes {n} créditos y tu amigo también.', { n: c.referidos.recompensa })}</span>
                <div className="mos-campo">
                  <Icono nombre="corazon" clase="ic-sm" />
                  <span className="mos-enlace" title={enlace}>{enlace.replace(/^https?:\/\//, '')}</span>
                  <button className="btn-negro" onClick={() => void copiar()}>{copiado ? t('¡Copiado!') : t('Copiar')}</button>
                </div>
                <span className="mos-pie">
                  <a href={`https://wa.me/?text=${encodeURIComponent(t('Te invito a IGOR, tu equipo de contenido con IA. Entra con mi enlace y recibe {n} créditos al comprar tu plan: {enlace}', { n: c.referidos.recompensa, enlace }))}`} target="_blank" rel="noreferrer"><Icono nombre="chat" clase="ic-sm" /> {t('Enviar por WhatsApp')}</a>
                  <span>{c.referidos.invitados === 1 ? t('1 invitado') : t('{n} invitados', { n: c.referidos.invitados })} · {c.referidos.compraron === 1 ? t('1 compró') : t('{n} compraron', { n: c.referidos.compraron })}</span>
                </span>
                {c.referidos.puedeUsarCodigo ? (
                  <form className="mos-codigo" onSubmit={usarCodigo}>
                    <span>{t('¿Te invitó un amigo?')}</span>
                    <input value={codigoAmigo} onChange={(e) => setCodigoAmigo(e.target.value.toUpperCase())} placeholder={t('Su código')} maxLength={16} aria-label={t('Código de tu amigo')} />
                    <button className="btn-negro" disabled={codigoAmigo.trim().length < 4}>{t('Usar')}</button>
                  </form>
                ) : c.referidos.invitadoPor === 'pendiente' ? (
                  <span className="mos-aviso"><Icono nombre="check" clase="ic-sm" /> {t('Llegaste con un amigo: al comprar tu plan, cada uno recibe {n} créditos.', { n: c.referidos.recompensa })}</span>
                ) : null}
                {errorCodigo && <span className="mos-error">{errorCodigo}</span>}
              </div>
            ) : (
              <div className="mos mos-plan">
                <Titular icono="estrella" texto={t('Tu plan')} />
                <b className="mos-nombre">{c.plan?.name ?? (c.workspace?.estado === 'trial' ? t('Prueba gratis') : t('Sin plan'))}</b>
                <span className="mos-texto">
                  {c.plan ? t('{precio} USD al mes · {n} créditos', { precio: num(Number(c.plan.price_usd_month), locale), n: num(c.plan.monthly_credits, locale) }) : t('Elige un plan para seguir creando cada mes.')}
                </span>
                <div className="mos-tarjetita">
                  {!c.plan && c.workspace?.estado === 'trial' && c.creditos?.vencenPronto
                    ? <span><span className="dato-etiqueta">{t('Tu prueba termina')}</span><b>{fecha(c.creditos.vencenPronto.fecha, locale)}</b></span>
                    : <span><span className="dato-etiqueta">{c.suscripcion?.cancel_at ? t('Termina') : t('Se renueva')}</span><b>{fecha(c.suscripcion?.cancel_at ?? c.suscripcion?.current_period_end, locale) || '—'}</b></span>}
                  <span><span className="dato-etiqueta">{t('Revisión humana')}</span><b>{c.plan?.human_review ? t('Incluida') : t('No incluida')}</b></span>
                </div>
              </div>
            )}

            <div className="mos mos-mes">
              <Titular icono="publicar" texto={t('Tu {mes}', { mes: nombreMes })} />
              <div className="mos-mes-cifras">
                <span><b className="num" data-cuenta={tot.prog}>{tot.prog}</b>{t('programadas')}</span>
                <span><b className="num" data-cuenta={tot.pub}>{tot.pub}</b>{t('publicadas')}</span>
                <span><b className="num" data-cuenta={tot.int}>{tot.int}</b>{t('interacciones')}</span>
              </div>
              <button className="mos-pildora" onClick={() => setComoFunciona(true)}>
                {t('Cómo funciona')} <span className="mas-negro"><Icono nombre="flecha-larga" clase="ic-sm" /></span>
              </button>
            </div>
          </section>

          <section className="col" style={{ gap: 14 }}>
            <div className="titulo-sec">
              <span className="col" style={{ gap: 2 }}>
                <h2>{t('Tus marcas')}</h2>
                <span className="apunte">{t('Cada una con su estrategia, su identidad y su propio mes.')}</span>
              </span>
            </div>
            <div className="rejilla-marcas">
              {marcas.map((m) => (
                <div key={m.id} className="tarjeta marca-resumen" {...sobre}>
                  <div className="marca-resumen-cab">
                    <Emblema id={m.id} nombre={m.name} logo={m.logoUrl} />
                    <span className="col" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                      <b className="tarjeta-titulo">{m.name}</b>
                      <span className="apunte marca-nicho">{m.niche ?? t('Sin definir todavía')}</span>
                    </span>
                    <button className="btn-icono" title={t('Borrar marca')} aria-label={t('Borrar {nombre}', { nombre: m.name })} onClick={() => void borrar(m)}>
                      <Icono nombre="papelera" clase="ic-sm" />
                    </button>
                  </div>
                  <div className="marca-avances">
                    <Avance titulo={t('Tu marca')} v={m.marcaLista} t={m.marcaTotal} />
                    <Avance titulo={t('Tu {mes}', { mes: nombreMes })} v={m.mesListo} t={m.mesTotal} />
                  </div>
                  <div className="marca-numeros">
                    <span><b className="num">{m.programadas}</b>{t('programadas')}</span>
                    <span><b className="num">{m.publicadas}</b>{t('publicadas')}</span>
                    <span><b className="num">{num(m.interacciones, locale)}</b>{t('interacciones')}</span>
                  </div>
                  <button className="btn" onClick={() => ir(`/marca/${m.id}`)}>{t('Abrir {nombre}', { nombre: m.name })} <Icono nombre="flecha-larga" clase="ic-sm" /></button>
                </div>
              ))}
            </div>
          </section>

        </>
      )}
      {!c && !error && <p className="apunte">{t('Cargando…')}</p>}
      {aBorrar && <BorrarMarca marca={aBorrar} cerrar={() => setABorrar(null)} borrada={async () => { setABorrar(null); await cargar(); }} />}
    </main>
  );
}

function Titular({ icono, texto }: { icono: NombreDeIcono; texto: string }) {
  return (
    <span className="mos-titular">
      <span className="mos-icono"><Icono nombre={icono} clase="ic-sm" /></span>
      {texto}
    </span>
  );
}

function Avance({ titulo, v, t }: { titulo: string; v: number; t: number }) {
  return (
    <div className="avance-marca">
      <span className="titulo-sec"><span className="dato-etiqueta">{titulo}</span><b className="num">{v}/{t}</b></span>
      <span className="segmentos">{Array.from({ length: t }, (_, k) => <i key={k} className={k < v ? 'hecho' : ''} />)}</span>
    </div>
  );
}

/** Confirmar el borrado escribiendo el nombre: el botón se activa cuando coincide y los errores salen aquí mismo. */
function BorrarMarca({ marca, cerrar, borrada }: { marca: MarcaResumen; cerrar: () => void; borrada: () => Promise<void> }) {
  const { t } = useIdioma();
  const [escrito, setEscrito] = useState('');
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState('');
  const coincide = escrito.trim().toLocaleLowerCase('es') === marca.name.trim().toLocaleLowerCase('es');
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape' && !borrando) cerrar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [borrando, cerrar]);
  async function confirmar(e: React.FormEvent) {
    e.preventDefault();
    if (!coincide) return;
    setBorrando(true);
    setError('');
    try {
      await api.borrarMarca(marca.id, escrito);
      await borrada();
    } catch (err) {
      setError((err as Error).message);
      setBorrando(false);
    }
  }
  return (
    <div className="dialogo-fondo" role="dialog" aria-modal="true" aria-label={t('Borrar {nombre}', { nombre: marca.name })} onClick={() => !borrando && cerrar()}>
      <form className="dialogo" onClick={(e) => e.stopPropagation()} onSubmit={confirmar}>
        <span className="dialogo-ico"><Icono nombre="papelera" /></span>
        <b className="dialogo-titulo">{t('¿Borrar «{nombre}»?', { nombre: marca.name })}</b>
        <p className="apunte">{t('Se borra todo: su estrategia, sus meses, textos, diseños, videos y archivos. No se puede deshacer y los créditos usados no se devuelven.')}</p>
        <label className="campo">
          <span>{t('Para confirmar, escribe su nombre: {nombre}', { nombre: marca.name })}</span>
          <input autoFocus value={escrito} onChange={(e) => setEscrito(e.target.value)} placeholder={marca.name} disabled={borrando} />
        </label>
        {error && <p className="error">{error}</p>}
        <span className="fila-acciones" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn claro" onClick={cerrar} disabled={borrando}>{t('Cancelar')}</button>
          <button className="btn peligro" disabled={!coincide || borrando}>{borrando ? t('Borrando…') : t('Borrar la marca')}</button>
        </span>
      </form>
    </div>
  );
}
